import { ChangeDetectionStrategy, Component, inject, input, model, OnInit, output, signal } from '@angular/core';
import { DsToastService } from '@bmw-ds/components';
import { NGXLogger } from 'ngx-logger';

import { DealerProvider } from '../../../../auth/dealer/dealer.provider';
import { Customer, makeBlankCustomer } from '../../../customer/model/customer.model';
import { FoundCustomer, FoundRetailCustomer, isRetailCustomer } from '../../../customer/model/found-customer.model';
import { CustomerInformationDialogContainer } from '../../../customer-information-dialog/components/customer-information-dialog.container';
import { SearchFormService } from '../../../customer-search-page/search-form.service';
import { useI18n } from '../../../i18n/services/i18n.service';
import { VerifyChoiceModalWrapper } from '../../../modal/verify-choice-modal/verify-choice-modal.wrapper';
import { CustomerForCreation } from '../../model/customer-for-creation';
import { CustomerDuplicateWarningComponent } from '../customer-duplicate-warning/customer-duplicate-warning.component';
import { CustomerNscSyncCheckboxComponent } from '../customer-nsc-sync-checkbox/customer-nsc-sync-checkbox.component';

@Component({
  selector: 'cdf-customer-creation-form',
  imports: [
    CustomerInformationDialogContainer,
    CustomerNscSyncCheckboxComponent,
    CustomerDuplicateWarningComponent,
    VerifyChoiceModalWrapper,
  ],
  template: `
    @if (duplicates(); as foundDuplicates) {
      <cdf-customer-duplicate-warning
        [duplicates]="foundDuplicates"
        [saving]="saving()"
        (backFromDuplicate)="duplicates.set(undefined)"
        (ignoreDuplicates)="createCustomer(customer(), true, $event)"
        (openDuplicateProfile)="loadAndOpenExistingProfile($event, false)"
      />
    } @else {
      <!-- "continuouslyValidate" is set to "false" to avoid displaying warnings before the user had time to enter information -->
      <cdf-customer-information-dialog-container
        [customer]="customer()"
        [saveButtonLabel]="i18n('customerCreation.button')"
        [continuouslyValidate]="false"
        (saveChanges)="createCustomer($event)"
        [(saving)]="saving"
        (closeModal)="closeModal.emit()"
      >
        <cdf-customer-nsc-sync-checkbox [disabled]="saving()" [(checked)]="syncToNscDatabase" />
      </cdf-customer-information-dialog-container>
    }
    <cdf-verify-choice-modal-wrapper
      [(open)]="askForImportConsentModalOpen"
      (verify)="openCustomerProfileWithConsent()"
      [headingText]="i18n('importToRetail.warningHeading')"
      [bodyText]="i18n('importToRetail.warningBody')"
      [verifyButtonText]="i18n('importToRetail.warningAgreeButton')"
      [buttonLoading]="verifyChoiceModalButtonsLoading()"
    />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerCreationFormContainer implements OnInit {
  protected readonly searchFormService = inject(SearchFormService);
  protected readonly dsToastService = inject(DsToastService);
  protected readonly dealerProvider = inject(DealerProvider);
  protected readonly logger = inject(NGXLogger);

  protected readonly i18n = useI18n();

  protected readonly customer = signal<Customer>(makeBlankCustomer());
  protected readonly syncToNscDatabase = signal(true);
  protected readonly askForImportConsentModalOpen = signal(false);
  protected readonly verifyChoiceModalButtonsLoading = signal(false);
  protected foundCustomerForConsent?: FoundCustomer;

  readonly saveCustomer = output<CustomerForCreation>();
  readonly openCustomerProfile = output<FoundRetailCustomer>();
  readonly closeModal = output();

  readonly saving = model(false);
  readonly initialCustomerInfo = input<Customer>();
  readonly duplicates = model<FoundCustomer[]>();

  private toastId = 'CUSTOMER_CREATION_FORM_CONTAINER_TOAST';

  ngOnInit() {
    this.setCustomerTaxCountry();
  }

  private setCustomerTaxCountry() {
    this.customer.update((customer) => ({
      ...customer,
      ...this.initialCustomerInfo(),

      // Initially set to country of user. However, can be changed in the UI via dropdown
      taxNumberCountry: this.dealerProvider.activeCountry(),
    }));
  }

  protected async loadAndOpenExistingProfile(
    foundCustomer: FoundCustomer,
    consentForImportGiven = false,
  ): Promise<void> {
    if (!isRetailCustomer(foundCustomer)) {
      const gcid = foundCustomer.gcid;
      if (gcid && consentForImportGiven) {
        foundCustomer.accountNumber = await this.searchFormService.fetchAccountNumberWithImport(gcid);
        if (!isRetailCustomer(foundCustomer)) {
          throw new Error('#CDFTF0091 No account number could be fetched.');
        }
      } else if (gcid && !consentForImportGiven) {
        // Store customer to access it later after consent of user for import to Retail was provided
        this.foundCustomerForConsent = foundCustomer;
        this.askForImportConsentModalOpen.set(true);
        return;
      } else {
        throw new Error('#CDFTF0101 Neither accountNumber not GCID could be fetched for customer.');
      }
    }
    this.openCustomerProfile.emit(foundCustomer);
  }

  protected async openCustomerProfileWithConsent() {
    try {
      if (this.foundCustomerForConsent) {
        this.verifyChoiceModalButtonsLoading.set(true);
        await this.loadAndOpenExistingProfile(this.foundCustomerForConsent, true);
      } else {
        console.error('#CDFTF0094 No customer set for importing customer to Retail CRM and opening profile page.');
        this.showOpenCustomerProfileError();
      }
    } catch {
      this.showOpenCustomerProfileError();
    } finally {
      this.verifyChoiceModalButtonsLoading.set(false);
    }
  }

  protected createCustomer(customer: Customer, ignoreDuplicate = false, canBeCreatedInNscDatabase = true) {
    // Saving is set to "false" again via the triggered function by emitting "saveCustomer"

    this.saving.set(true);
    const market = this.dealerProvider.activeCountry();

    if (!market) {
      this.logger.error('#CDFTF0185 No market set for customer at CustomerCreationFormContainer.createCustomer.');
      return;
    }

    this.customer.set(customer); // Save changes so that they remain when switching back tabs
    this.saveCustomer.emit({
      customer: customer,
      market,
      syncToNscDatabase: this.syncToNscDatabase() && canBeCreatedInNscDatabase,
      ignoreDuplicate,
    });
  }

  private showOpenCustomerProfileError() {
    this.dsToastService.pushToast({
      toastTitle: this.i18n('searchResults.openProfileErrorTitle'),
      toastText: this.i18n('searchResults.openProfileErrorMessage'),
      id: this.toastId,
      tone: 'critical',
    });
  }
}
